import React, { useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloud, X, FileText, ImageIcon, Loader2, CheckCircle2, Wrench, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useLang } from '@/context/LanguageContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const MAX_FILES = 10;
const ACCEPT = 'image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf';

const prettySize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function RepairRequestForm() {
  const { t } = useLang();
  const R = t('repair.form');
  const inputRef = useRef(null);

  const empty = {
    device_type: '', manufacturer: '', model: '', serial_number: '',
    powers_on: 'yes', urgency: '', preferred_date: '', error_description: '',
    name: '', email: '', phone: '', company: '',
  };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]); // {localId, name, size, status, meta?}
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const uploadFile = useCallback(async (file, localId) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/leads/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: 'done', meta: res.data.file } : f)));
    } catch (err) {
      setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: 'error' } : f)));
      toast.error(err?.response?.data?.detail || R.errorUpload);
    }
  }, [R.errorUpload]);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList);
    setFiles((prev) => {
      const room = MAX_FILES - prev.length;
      const toAdd = incoming.slice(0, Math.max(0, room)).map((file) => {
        const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        uploadFile(file, localId);
        return { localId, name: file.name, size: file.size, type: file.type, status: 'uploading' };
      });
      return [...prev, ...toAdd];
    });
  }, [uploadFile]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const removeFile = (localId) => setFiles((prev) => prev.filter((f) => f.localId !== localId));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.device_type || !form.error_description || !form.name || !form.email) {
      toast.error(R.errorRequired); return;
    }
    if (files.some((f) => f.status === 'uploading')) { toast.error(R.uploading); return; }
    setSubmitting(true);
    try {
      const attachments = files.filter((f) => f.status === 'done' && f.meta).map((f) => f.meta);
      await api.post('/leads', {
        ...form,
        type: 'repair',
        service: 'geraete-reparatur',
        language: t('nav.services') === 'Services' ? 'en' : 'de',
        source_page: '/leistungen/geraete-reparatur',
        attachments,
      });
      setDone(true);
      toast.success(R.successTitle);
    } catch (err) {
      toast.error(R.errorUpload);
    } finally { setSubmitting(false); }
  };

  const reset = () => { setForm(empty); setFiles([]); setDone(false); };

  const inputCls = 'bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)] focus-visible:ring-[rgba(90,243,255,0.4)]';
  const sectionCls = 'rounded-2xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/60 p-5 sm:p-6';

  if (done) {
    return (
      <div className="glass rounded-2xl p-10 text-center" data-testid="repair-form-success-message">
        <div className="mx-auto h-16 w-16 rounded-full grid place-items-center bg-[color:var(--itw-cyan)]/12 border border-[color:var(--itw-border-strong)]">
          <CheckCircle2 className="h-9 w-9 text-[color:var(--itw-cyan)]" />
        </div>
        <h3 className="mt-5 font-display text-xl font-semibold text-[color:var(--itw-text)]">{R.successTitle}</h3>
        <p className="mt-2 text-[color:var(--itw-text-2)] max-w-md mx-auto">{R.success}</p>
        <Button onClick={reset} data-testid="repair-form-new-request"
          className="mt-6 rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] neon-edge hover:opacity-90 transition-opacity">{R.newRequest}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="repair-request-form">
      {/* DEVICE SECTION */}
      <div className={sectionCls}>
        <div className="flex items-center gap-2 mb-4">
          <Wrench className="h-4 w-4 text-[color:var(--itw-cyan)]" />
          <h3 className="font-display font-semibold text-[color:var(--itw-text)]">{R.deviceSection}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{R.deviceType} *</Label>
            <Select value={form.device_type} onValueChange={(v) => setForm((f) => ({ ...f, device_type: v }))}>
              <SelectTrigger data-testid="repair-form-device-type-select" className={inputCls}><SelectValue placeholder={R.deviceTypePlaceholder} /></SelectTrigger>
              <SelectContent>
                {R.deviceTypes.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{R.urgency}</Label>
            <Select value={form.urgency} onValueChange={(v) => setForm((f) => ({ ...f, urgency: v }))}>
              <SelectTrigger data-testid="repair-form-urgency-select" className={inputCls}><SelectValue placeholder={R.urgencyPlaceholder} /></SelectTrigger>
              <SelectContent>
                {R.urgencyOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{R.manufacturer}</Label>
            <Input value={form.manufacturer} onChange={set('manufacturer')} placeholder={R.manufacturerPlaceholder} data-testid="repair-form-manufacturer-input" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label>{R.model}</Label>
            <Input value={form.model} onChange={set('model')} placeholder={R.modelPlaceholder} data-testid="repair-form-model-input" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label>{R.serial}</Label>
            <Input value={form.serial_number} onChange={set('serial_number')} placeholder={R.serialPlaceholder} data-testid="repair-form-serial-number-input" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label>{R.preferredDate}</Label>
            <Input type="date" value={form.preferred_date} onChange={set('preferred_date')} data-testid="repair-form-preferred-date-button" className={inputCls} />
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <Label>{R.powersOn}</Label>
          <RadioGroup value={form.powers_on} onValueChange={(v) => setForm((f) => ({ ...f, powers_on: v }))} className="flex flex-wrap gap-2">
            {[['yes', R.powersYes], ['no', R.powersNo], ['unknown', R.powersUnknown]].map(([val, label]) => {
              const sel = form.powers_on === val;
              const tid = val === 'yes' ? 'repair-form-powers-on-yes-radio' : val === 'no' ? 'repair-form-powers-on-no-radio' : 'repair-form-powers-on-unknown-radio';
              return (
                <label key={val} data-testid={tid}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm cursor-pointer transition-[border-color,background-color] duration-150 ${sel ? 'border-[color:var(--itw-cyan)] bg-[color:var(--itw-cyan)]/10 text-[color:var(--itw-text)]' : 'border-[color:var(--itw-border)] text-[color:var(--itw-text-2)] hover:border-[color:var(--itw-border-strong)]'}`}>
                  <RadioGroupItem value={val} className="border-[color:var(--itw-border-strong)] text-[color:var(--itw-cyan)]" />
                  {label}
                </label>
              );
            })}
          </RadioGroup>
        </div>
        <div className="mt-4 space-y-1.5">
          <Label>{R.errorDescription} *</Label>
          <Textarea rows={4} value={form.error_description} onChange={set('error_description')} placeholder={R.errorPlaceholder} data-testid="repair-form-error-description-textarea" className={inputCls} />
        </div>
      </div>

      {/* CONTACT SECTION */}
      <div className={sectionCls}>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-[color:var(--itw-cyan)]" />
          <h3 className="font-display font-semibold text-[color:var(--itw-text)]">{R.contactSection}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>{R.name} *</Label><Input value={form.name} onChange={set('name')} required data-testid="repair-form-customer-name-input" className={inputCls} /></div>
          <div className="space-y-1.5"><Label>{R.email} *</Label><Input type="email" value={form.email} onChange={set('email')} required data-testid="repair-form-customer-email-input" className={inputCls} /></div>
          <div className="space-y-1.5"><Label>{R.phone}</Label><Input value={form.phone} onChange={set('phone')} data-testid="repair-form-customer-phone-input" className={inputCls} /></div>
          <div className="space-y-1.5"><Label>{R.company}</Label><Input value={form.company} onChange={set('company')} data-testid="repair-form-customer-company-input" className={inputCls} /></div>
        </div>
      </div>

      {/* ATTACHMENTS SECTION */}
      <div className={sectionCls}>
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-4 w-4 text-[color:var(--itw-cyan)]" />
          <h3 className="font-display font-semibold text-[color:var(--itw-text)]">{R.attachSection}</h3>
        </div>
        <div
          role="button" tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="repair-form-attachments-dropzone"
          className={`relative rounded-2xl border border-dashed p-6 text-center cursor-pointer transition-[border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(90,243,255,0.55)] ${dragOver ? 'border-[color:var(--itw-border-strong)] shadow-[0_0_0_1px_rgba(90,243,255,0.22),0_0_28px_rgba(0,231,255,0.14)]' : 'border-[color:var(--itw-border)]'} bg-[rgba(11,16,23,0.55)]`}>
          <UploadCloud className="mx-auto h-8 w-8 text-[color:var(--itw-cyan-soft)]" />
          <p className="mt-2 text-sm text-[color:var(--itw-text-2)]">{R.dropzoneIdle}</p>
          <span className="mt-3 inline-flex items-center rounded-full border border-[color:var(--itw-border)] px-3 py-1.5 text-xs text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)]" data-testid="repair-form-attachments-browse-button">{R.dropzoneBrowse}</span>
          <p className="mt-2 text-[11px] text-[color:var(--itw-text-3)] font-mono">{R.dropzoneHelper}</p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
            onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
        </div>
        <AnimatePresence>
          {files.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-3 flex flex-wrap gap-2">
              {files.map((f) => (
                <div key={f.localId} data-testid="repair-form-file-chip"
                  className="flex items-center gap-2 rounded-full border border-[color:var(--itw-border)] bg-[rgba(15,22,33,0.65)] px-3 py-1.5 text-xs text-[color:var(--itw-text-2)]">
                  {f.type === 'application/pdf' ? <FileText className="h-3.5 w-3.5 text-[color:var(--itw-text-3)]" /> : <ImageIcon className="h-3.5 w-3.5 text-[color:var(--itw-text-3)]" />}
                  <span className="max-w-[160px] truncate text-[color:var(--itw-text)]">{f.name}</span>
                  <span className="text-[color:var(--itw-text-3)]">{prettySize(f.size)}</span>
                  {f.status === 'uploading' && <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--itw-cyan-soft)]" />}
                  {f.status === 'done' && <CheckCircle2 className="h-3.5 w-3.5 text-[#2EF2A5]" />}
                  {f.status === 'error' && <span className="text-[#FF5C7A]">!</span>}
                  <button type="button" onClick={() => removeFile(f.localId)} data-testid={`repair-form-file-remove-${f.localId}`}
                    className="ml-0.5 text-[color:var(--itw-text-3)] hover:text-[#FF5C7A] transition-colors" aria-label={R.removeFile}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-[color:var(--itw-text-3)] font-mono"><Clock className="h-3.5 w-3.5" /> {t('contact.responseTime')}</div>
        <Button type="submit" disabled={submitting} data-testid="repair-form-submit-button"
          className="h-12 px-8 rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{R.sending}</> : R.submit}
        </Button>
      </div>
    </form>
  );
}
